import { Component, Input, OnInit, ChangeDetectionStrategy, inject } from '@angular/core';
import { IdentityVerificationSource } from '../../interfaces/identity-verification-source/identity-verification-source';
import { eIdentityVerificationResult, eIdentityVerificationSource, eIdentityVerificationValues } from '../../enums';
import { IdentityVerificationDetails } from '../../interfaces/identity-verification-details/identity-verification-details';
import { TranslocoService } from '@jsverse/transloco';

interface SourceTable {
  header: string;
  sources: IdentityVerificationSource[];
  rows: { label: string; values: string[]; }[];
}

interface RowDefinition {
  label: string;
  getValue: (s: IdentityVerificationSource) => string | null | undefined;
  isFailed?: (v: IdentityVerificationSource) => boolean;
}

export const AML_SOURCES: eIdentityVerificationSource[] = [
  eIdentityVerificationSource.EfxAmlSingleSource,
  eIdentityVerificationSource.EfxAmlDualSourceFirst,
  eIdentityVerificationSource.EfxAmlDualSourceSecond,
  eIdentityVerificationSource.TuCreditBureau,
  eIdentityVerificationSource.TuCreditBureauFirst,
  eIdentityVerificationSource.TuCreditBureauSecond
];

export const DVS_SOURCES: eIdentityVerificationSource[] = [
  eIdentityVerificationSource.EfxDvs,
  eIdentityVerificationSource.EntrustDvs,
];

const SOURCE_LABEL_MAP: Partial<Record<eIdentityVerificationSource, string>> = {
  [eIdentityVerificationSource.EfxAmlSingleSource]: 'Credit File',
  [eIdentityVerificationSource.EfxAmlDualSourceFirst]: 'First Source',
  [eIdentityVerificationSource.EfxAmlDualSourceSecond]: 'Second Source',
  [eIdentityVerificationSource.EfxDvs]: 'Document Verification (DVS)',
  [eIdentityVerificationSource.EntrustDvs]: 'Document Verification (DVS)',
};

const SOURCE_LABEL_KEYS: Record<string, string> = {
  'Credit File': 'idvTooltip.sources.creditFile',
  'First Source': 'idvTooltip.sources.firstSource',
  'Second Source': 'idvTooltip.sources.secondSource',
  'Document Verification (DVS)': 'idvTooltip.sources.documentVerificationDvs',
};

const defaultFailed = (v: string) =>
  v === eIdentityVerificationValues.Fail ||
  v === eIdentityVerificationValues.NoMatch ||
  v === eIdentityVerificationValues.PartialMatch ||
  v === eIdentityVerificationValues.False;

const inverseDefaultFailed = (v: string) =>
  v === eIdentityVerificationValues.Pass ||
  v === eIdentityVerificationValues.True;

const addressNotMatched = (s: IdentityVerificationSource) => {
  return defaultFailed(s.addressMatch ?? "") ||
    defaultFailed(s.unitNumberMatch ?? "") ||
    defaultFailed(s.civicNumberMatch ?? "") ||
    defaultFailed(s.streetNameMatch ?? "") ||
    defaultFailed(s.cityMatch ?? "") ||
    defaultFailed(s.jurisdictionMatch ?? "") ||
    defaultFailed(s.postalCodeMatch ?? "");
};

const ROW_LABEL_KEYS: Record<string, string> = {
  'Name As Reported': 'idvTooltip.rows.nameAsReported',
  'First Name As Reported': 'idvTooltip.rows.firstNameAsReported',
  'Middle Name As Reported': 'idvTooltip.rows.middleNameAsReported',
  'Last Name As Reported': 'idvTooltip.rows.lastNameAsReported',
  'Date of Birth As Reported': 'idvTooltip.rows.dateOfBirthAsReported',
  'Address As Reported': 'idvTooltip.rows.addressAsReported',
  'Document Liveliness Match': 'idvTooltip.rows.documentLivelinessMatch',
  'Liveliness': 'idvTooltip.rows.liveliness',
  'Name Match': 'idvTooltip.rows.nameMatch',
  'First Name Match': 'idvTooltip.rows.firstNameMatch',
  'Middle Name Match': 'idvTooltip.rows.middleNameMatch',
  'Last Name Match': 'idvTooltip.rows.lastNameMatch',
  'Date of Birth Match': 'idvTooltip.rows.dateOfBirthMatch',
  'Address Match': 'idvTooltip.rows.addressMatch',
  'Unit Number Match': 'idvTooltip.rows.unitNumberMatch',
  'Civic Number Match': 'idvTooltip.rows.civicNumberMatch',
  'Street Name Match': 'idvTooltip.rows.streetNameMatch',
  'City Match': 'idvTooltip.rows.cityMatch',
  'Jurisdiction Match': 'idvTooltip.rows.jurisdictionMatch',
  'Postal Code Match': 'idvTooltip.rows.postalCodeMatch',
  'Credit Bureau Hit Status': 'idvTooltip.rows.creditBureauHitStatus',
  'Credit Bureau Match': 'idvTooltip.rows.creditBureauMatch',
  'Credit File Older Than Six Months': 'idvTooltip.rows.creditFileOlderThanSixMonths',
  '2+ Trades Older Than Six Months': 'idvTooltip.rows.n2TradesOlderThanSixMonths',
  'More Than One Distinct Source': 'idvTooltip.rows.moreThanOneDistinctSource',
  'Age Of Credit File 3+ Years Old': 'idvTooltip.rows.ageOfCreditFile3YearsOld',
  'Document Expired': 'idvTooltip.rows.documentExpired',
  'Document Supported': 'idvTooltip.rows.documentSupported',
  'Document Quality': 'idvTooltip.rows.documentQuality',
  'Vendor Message': 'idvTooltip.rows.vendorMessage',
};

const ALL_ROWS: RowDefinition[] = [
  { label: 'Name As Reported', getValue: s => s.nameAsReported, isFailed: (s: IdentityVerificationSource) => { return defaultFailed(s.nameMatch ?? ""); } },
  { label: 'First Name As Reported', getValue: s => s.firstNameAsReported, isFailed: (s: IdentityVerificationSource) => { return defaultFailed(s.firstNameMatch ?? ""); } },
  { label: 'Middle Name As Reported', getValue: s => s.middleNameAsReported, isFailed: (s: IdentityVerificationSource) => { return defaultFailed(s.middleNameMatch ?? ""); } },
  { label: 'Last Name As Reported', getValue: s => s.lastNameAsReported, isFailed: (s: IdentityVerificationSource) => { return defaultFailed(s.lastNameMatch ?? ""); } },
  { label: 'Date of Birth As Reported', getValue: s => s.dobAsReported ? new Date(s.dobAsReported).toISOString().split('T')[0] : null, isFailed: (s: IdentityVerificationSource) => { return defaultFailed(s.dobMatch ?? ""); } },
  { label: 'Address As Reported', getValue: s => s.addressAsReported, isFailed: addressNotMatched },
  { label: 'Document Liveliness Match', getValue: s => s.documentLiveleynessMatch, isFailed: (s: IdentityVerificationSource) => { return defaultFailed(s.documentLiveleynessMatch ?? ""); } },
  { label: 'Liveliness', getValue: s => s.liveliness, isFailed: (s: IdentityVerificationSource) => { return defaultFailed(s.liveliness ?? ""); } },
  { label: 'Name Match', getValue: s => s.nameMatch, isFailed: (s: IdentityVerificationSource) => { return defaultFailed(s.nameMatch ?? ""); } },
  { label: 'First Name Match', getValue: s => s.firstNameMatch, isFailed: (s: IdentityVerificationSource) => { return defaultFailed(s.firstNameMatch ?? ""); } },
  { label: 'Middle Name Match', getValue: s => s.middleNameMatch, isFailed: (s: IdentityVerificationSource) => { return defaultFailed(s.middleNameMatch ?? ""); } },
  { label: 'Last Name Match', getValue: s => s.lastNameMatch, isFailed: (s: IdentityVerificationSource) => { return defaultFailed(s.lastNameMatch ?? ""); } },
  { label: 'Date of Birth Match', getValue: s => s.dobMatch, isFailed: (s: IdentityVerificationSource) => { return defaultFailed(s.dobMatch ?? ""); } },
  { label: 'Address Match', getValue: s => s.addressMatch, isFailed: (s: IdentityVerificationSource) => { return defaultFailed(s.addressMatch ?? ""); } },
  { label: 'Unit Number Match', getValue: s => s.unitNumberMatch, isFailed: (s: IdentityVerificationSource) => { return defaultFailed(s.unitNumberMatch ?? ""); } },
  { label: 'Civic Number Match', getValue: s => s.civicNumberMatch, isFailed: (s: IdentityVerificationSource) => { return defaultFailed(s.civicNumberMatch ?? ""); } },
  { label: 'Street Name Match', getValue: s => s.streetNameMatch, isFailed: (s: IdentityVerificationSource) => { return defaultFailed(s.streetNameMatch ?? ""); } },
  { label: 'City Match', getValue: s => s.cityMatch, isFailed: (s: IdentityVerificationSource) => { return defaultFailed(s.cityMatch ?? ""); } },
  { label: 'Jurisdiction Match', getValue: s => s.jurisdictionMatch, isFailed: (s: IdentityVerificationSource) => { return defaultFailed(s.jurisdictionMatch ?? ""); } },
  { label: 'Postal Code Match', getValue: s => s.postalCodeMatch, isFailed: (s: IdentityVerificationSource) => { return defaultFailed(s.postalCodeMatch ?? ""); } },
  { label: 'Credit Bureau Hit Status', getValue: s => s.creditBureauHitStatus, isFailed: (s: IdentityVerificationSource) => { return defaultFailed(s.creditBureauHitStatus ?? ""); } },
  { label: 'Credit Bureau Match', getValue: s => s.creditBureauMatch, isFailed: (s: IdentityVerificationSource) => { return defaultFailed(s.creditBureauMatch ?? ""); } },
  { label: 'Credit File Older Than Six Months', getValue: s => s.creditFileIsOlderThanSixMonths, isFailed: (s: IdentityVerificationSource) => { return defaultFailed(s.creditFileIsOlderThanSixMonths ?? ""); } },
  { label: '2+ Trades Older Than Six Months', getValue: s => s.twoPlusTradesOlderThanSixMonths, isFailed: (s: IdentityVerificationSource) => { return defaultFailed(s.twoPlusTradesOlderThanSixMonths ?? ""); } },
  { label: 'More Than One Distinct Source', getValue: s => s.moreThanOneDistinctSource, isFailed: (s: IdentityVerificationSource) => { return defaultFailed(s.moreThanOneDistinctSource ?? ""); } },
  { label: 'Age Of Credit File 3+ Years Old', getValue: s => s.ageOfCreditFileThreeOrMoreYearsOld, isFailed: (s: IdentityVerificationSource) => { return defaultFailed(s.ageOfCreditFileThreeOrMoreYearsOld ?? ""); } },
  { label: 'Document Expired', getValue: s => s.documentExpired, isFailed: (s: IdentityVerificationSource) => { return inverseDefaultFailed(s.documentExpired ?? ""); } },
  { label: 'Document Supported', getValue: s => s.documentSupported, isFailed: (s: IdentityVerificationSource) => { return defaultFailed(s.documentSupported ?? ""); } },
  { label: 'Document Quality', getValue: s => s.documentQuality, isFailed: (s: IdentityVerificationSource) => { return defaultFailed(s.documentQuality ?? ""); } },
  { label: 'Vendor Message', getValue: s => s.vendorResponse, isFailed: (s: IdentityVerificationSource) => { return s.vendorResponse != null; } },
];

@Component({
  selector: 'app-idv-tooltip',
  templateUrl: './idv-tooltip.component.html',
  styleUrl: './idv-tooltip.component.css',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class IdvTooltipComponent implements OnInit {
  private readonly transloco = inject(TranslocoService);

  @Input() idvDetails?: IdentityVerificationDetails;

  tables: SourceTable[] = [];

  ngOnInit(): void {
    this.tables = this.buildTables();
  }

  public buildTables(): SourceTable[] {
    const sources = this.idvDetails?.sources ?? [];
    const result: SourceTable[] = [];

    const aml = sources.filter(s => AML_SOURCES.includes(s.name) && s.status != eIdentityVerificationResult.NA);
    const dvs = sources.filter(s => DVS_SOURCES.includes(s.name) && s.status != eIdentityVerificationResult.NA);

    if (aml.length) result.push(this.buildTable(aml));
    if (dvs.length) result.push(this.buildTable(dvs));

    return result;
  }

  private buildTable(sources: IdentityVerificationSource[]): SourceTable {
    const rows = ALL_ROWS
      .filter(row => sources.some(s => row.getValue(s) != null))
      .filter(row => {
        if (!row.isFailed) return true; // no filter defined, always show
        return sources.some(s => {
          return row.isFailed!(s);
        });
      })
      .map(row => ({
        label: this.transloco.translate(ROW_LABEL_KEYS[row.label] ?? row.label),
        values: sources.map(s => s.status == eIdentityVerificationResult.Success ? '-' : row.getValue(s) ?? '-')
      }));

    return { header: "x", sources: sources, rows };
  }

  formatCommasForLineBreaks(value: string): string {
    return value.replace(/,/g, ',<wbr>');
  }

  getSourceLabel(name: eIdentityVerificationSource): string {
    const label = SOURCE_LABEL_MAP[name];
    return label ? this.transloco.translate(SOURCE_LABEL_KEYS[label] ?? label) : name;
  }

  getStatusClass(status: eIdentityVerificationResult): string {
    switch (status) {
      case eIdentityVerificationResult.Success: return 'status-success';
      case eIdentityVerificationResult.Fail: return 'status-fail';
      case eIdentityVerificationResult.Pending: return 'status-pending';
      default: return 'status-na';
    }
  }
};